import { Link } from "react-router-dom";
import { BasilLock } from "@/components/icons/basil";
import { PioneerHoloCard } from "@shared/components/PioneerHoloCard";
import { cn } from "@shared/lib/utils";
import { Skeleton } from "@/components/ui/Skeleton";
import type { AgentCardItem } from "@/lib/api";
import { cardArtSrc } from "@/lib/cardArt";
import { CARD_FINISHES, type CardFinishMeta } from "@/lib/cards";
import type { HomeAgent } from "@/lib/homeAgents";

type CardStreamProps = {
  agent: HomeAgent;
  /** All catalog items (every agent); null = signed out, OR authenticated but never loaded. */
  items: AgentCardItem[] | null;
  loading: boolean;
  /** listAgentCards() failed and `items` never resolved — distinct from a confirmed-empty collection. */
  error: boolean;
  onSignIn: () => void;
  onRetry: () => void;
};

type Slot = { meta: CardFinishMeta; item: AgentCardItem | undefined };

/** Owned finish — muted holo tile + finish label + ×count badge. */
function OwnedTile({ agent, slot }: { agent: HomeAgent; slot: Slot }) {
  const count = slot.item?.count ?? 0;
  return (
    <figure className="flex flex-col gap-1.5">
      <div className="relative">
        <PioneerHoloCard
          // 실카드 아트 우선 — 컬렉션 FinishCardTile과 동일 패턴, 미존재 조합은 초상 폴백
          imageSrc={cardArtSrc(agent.agentId, slot.meta.finish) ?? agent.portrait}
          imageAlt={`${agent.name} — ${slot.meta.label}`}
          effect={slot.meta.holoEffect}
          muted
          idleOpacityScale={0.55}
          loading="lazy"
        />
        {count > 1 ? (
          <span className="pointer-events-none absolute right-1.5 top-1.5 z-10 rounded-full border border-line bg-black/70 px-2 py-0.5 font-mono text-[9px] font-medium tabular-nums text-white/85">
            ×{count}
          </span>
        ) : null}
      </div>
      <figcaption
        className={cn(
          "truncate px-0.5 font-mono text-[10px] font-medium uppercase leading-snug tracking-[0.08em]",
          slot.meta.accentText,
        )}
      >
        {slot.meta.label}
      </figcaption>
    </figure>
  );
}

/** Compact locked placeholder — lock glyph + finish label, no holo. */
function LockedSlot({ meta }: { meta: CardFinishMeta }) {
  return (
    <figure aria-label={`${meta.label} — not collected`} className="flex flex-col gap-1.5">
      <div className="grid aspect-[723/1024] place-items-center rounded-2xl border border-white/8 bg-white/[0.03]">
        <BasilLock className="size-4 text-white/25" aria-hidden />
      </div>
      <figcaption className="truncate px-0.5 font-mono text-[10px] font-medium uppercase leading-snug tracking-[0.08em] text-white/35">
        {meta.label}
      </figcaption>
    </figure>
  );
}

/**
 * Collection showcase — right zone. The vertical stream of MY cards for the
 * selected assistant.
 *
 * - Static scrollable grid of all 8 finish slots — owned holo tiles + compact
 *   locked placeholders. A bottom fade mask signals there's more to scroll to
 *   instead of hard-clipping. (No marquee: the S1 CardSwap + S3 ticker already
 *   exhaust the home page's always-on loop budget, spec §0-1/§9.1-1.)
 * - Zero owned / signed out / failed to load: dimmed locked grid + one-line
 *   message (sign-in CTA, "no cards yet" CTA, or a retry affordance — never
 *   the same copy for "confirmed empty" and "couldn't load").
 */
export function CardStream({ agent, items, loading, error, onSignIn, onRetry }: CardStreamProps) {
  if (loading) {
    return (
      <div className="grid h-[480px] grid-cols-2 content-start gap-3 overflow-hidden p-4 lg:h-full lg:p-5">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <Skeleton className="aspect-[723/1024] rounded-2xl" />
            <Skeleton className="h-2.5 w-16" />
          </div>
        ))}
      </div>
    );
  }

  const slots: Slot[] = CARD_FINISHES.map((meta) => ({
    meta,
    item: items?.find((item) => item.agentId === agent.agentId && item.rarity === meta.finish),
  }));
  const ownedSlots = slots.filter((slot) => slot.item?.owned);
  const neverLoaded = items === null;
  const signedOut = neverLoaded && !error;
  const failedToLoad = neverLoaded && error;
  const empty = signedOut || failedToLoad || ownedSlots.length === 0;

  // Static stream — capped height on mobile, fills the zone on lg+. Bottom
  // fade mask signals there's a clipped row below instead of hard-cutting it.
  return (
    <div
      key={agent.agentId}
      aria-label={`${agent.name} — collected cards`}
      className="h-[520px] overflow-y-auto [mask-image:linear-gradient(to_bottom,black_calc(100%-28px),transparent)] lg:h-full"
    >
      {empty ? (
        <p className="px-4 pt-4 text-xs leading-relaxed text-ink-dim lg:px-5 lg:pt-5">
          {signedOut ? (
            <>
              <button
                type="button"
                onClick={onSignIn}
                className="inline-block p-3.5 -m-3.5 font-mono font-medium uppercase tracking-[0.12em] text-accent transition-colors duration-200 hover:text-accent-2"
              >
                Sign in with Telegram
              </button>{" "}
              <span aria-hidden>→</span> track your {agent.name} cards.
            </>
          ) : failedToLoad ? (
            <>
              Couldn&apos;t load your cards.{" "}
              <button
                type="button"
                onClick={onRetry}
                className="inline-block p-3.5 -m-3.5 font-mono font-medium uppercase tracking-[0.12em] text-accent transition-colors duration-200 hover:text-accent-2"
              >
                Retry
              </button>
            </>
          ) : (
            <>
              No {agent.name} cards yet.{" "}
              <Link
                to="/shop"
                className="inline-block p-3.5 -m-3.5 font-mono font-medium uppercase tracking-[0.12em] text-accent transition-colors duration-200 hover:text-accent-2"
              >
                Open packs <span aria-hidden>→</span>
              </Link>
            </>
          )}
        </p>
      ) : null}

      <div
        className={cn(
          "grid grid-cols-2 gap-3 p-4 lg:p-5",
          empty && "opacity-60",
        )}
      >
        {slots.map((slot) =>
          slot.item?.owned ? (
            <OwnedTile key={slot.meta.finish} agent={agent} slot={slot} />
          ) : (
            <LockedSlot key={slot.meta.finish} meta={slot.meta} />
          ),
        )}
      </div>
    </div>
  );
}
